const ONGOING_VALUE = 0;
const REPEAT_DELAY_MS = 400;
const REPEAT_INTERVAL_MS = 80;
const NS = '.durationControl';
const CLAMP_ANIMATION_MS = 320;

const internalUpdates = new WeakSet();
const clampRuns = new WeakMap();

let stopActiveHold = null;

window.addEventListener('blur', () => {
    if (stopActiveHold) {
        stopActiveHold();
    }
});

function parts(control) {
    const value = control.querySelector('.duration-control__value');
    const slider = control.querySelector('.duration-control__slider');
    const number = control.querySelector('.duration-control__number');

    if (!value || !number) {
        throw new Error('duration-control: markup is missing the value or number field.');
    }

    return {
        value,
        slider,
        number,
        ongoing: control.querySelector('.duration-control__ongoing-input'),
        steps: control.querySelectorAll('[data-duration-step]'),
    };
}

function minOf(control) {
    const min = Number(control.dataset.min);
    if (!Number.isFinite(min) || min < 0) {
        throw new Error('duration-control: data-min must be a positive number.');
    }
    return min;
}

function maxInstallmentsByFrequency(control) {
    try {
        return JSON.parse(control.dataset.frequencyMaxInstallments || '{}');
    } catch (error) {
        throw new Error('duration-control: data-frequency-max-installments must be valid JSON.');
    }
}

function selectedFrequencyId(control) {
    const scope = control.closest('form') || document;
    const select = scope.querySelector('select[name*="frequency"]');
    if (select) {
        const option = select.options[select.selectedIndex];
        return String((option && option.id) || select.value);
    }

    const hidden = document.getElementById('frequency_id');
    return hidden ? String(hidden.value) : '';
}

function maxOf(control) {
    const max = Number(control.dataset.max);
    if (!Number.isFinite(max)) {
        throw new Error('duration-control: data-max must be a number.');
    }

    const frequencyMax = maxInstallmentsByFrequency(control)[selectedFrequencyId(control)];

    return Number.isFinite(frequencyMax) ? Math.min(max, frequencyMax) : max;
}

function clamp(control, value) {
    return Math.min(maxOf(control), Math.max(minOf(control), value));
}

function isOngoing(control) {
    const { value } = parts(control);
    return value.value !== '' && Number(value.value) === ONGOING_VALUE;
}

function lastNumericValue(control) {
    const stored = Number(control.dataset.lastNumeric);
    return Number.isFinite(stored) && stored > 0 ? clamp(control, stored) : minOf(control);
}

function render(control) {
    const { value, slider, number, ongoing, steps } = parts(control);
    const ongoingSelected = isOngoing(control);
    const disabled = control.classList.contains('is-disabled');
    const locked = disabled || ongoingSelected;
    const numeric = value.value === '' || ongoingSelected
        ? null
        : clamp(control, parseInt(value.value, 10) || minOf(control));

    if (numeric !== null) {
        control.dataset.lastNumeric = String(numeric);
    }

    if (slider) {
        slider.min = String(minOf(control));
        slider.max = String(maxOf(control));
        slider.value = String(numeric === null ? lastNumericValue(control) : numeric);
        slider.disabled = locked;
        const span = Number(slider.max) - Number(slider.min);
        const filled = span > 0 ? ((Number(slider.value) - Number(slider.min)) / span) * 100 : 0;
        control.style.setProperty('--duration-fill', `${filled}%`);
    }

    if (document.activeElement !== number) {
        number.value = numeric === null ? '' : String(numeric);
    }

    if (ongoing) {
        ongoing.checked = ongoingSelected;
    }

    number.disabled = locked;
    steps.forEach((step) => {
        step.disabled = locked;
    });
    if (ongoing) {
        ongoing.disabled = disabled;
    }

    control.classList.toggle('is-ongoing', ongoingSelected);
}

function commit(control, nextValue) {
    const { value } = parts(control);
    const next = nextValue === null ? '' : String(nextValue);
    const changed = value.value !== next;

    value.value = next;
    render(control);

    if (!changed) {
        return;
    }

    internalUpdates.add(value);
    value.dispatchEvent(new Event('change', { bubbles: true }));
    internalUpdates.delete(value);
}

function commitNumeric(control, rawValue) {
    commit(control, clamp(control, parseInt(rawValue, 10) || minOf(control)));
}

function stepBy(control, delta) {
    if (isOngoing(control) || control.classList.contains('is-disabled')) {
        return;
    }
    const { value } = parts(control);
    if (value.value === '') {
        commitNumeric(control, lastNumericValue(control));
        return;
    }
    commitNumeric(control, parseInt(value.value, 10) + delta);
}

function bindHold(control, button) {
    let delayTimer = null;
    let repeatTimer = null;

    const stop = () => {
        clearTimeout(delayTimer);
        clearInterval(repeatTimer);
        delayTimer = null;
        repeatTimer = null;
        if (stopActiveHold === stop) {
            stopActiveHold = null;
        }
    };

    button.addEventListener('pointerdown', (event) => {
        if (event.button !== 0 || button.disabled) {
            return;
        }
        if (typeof button.setPointerCapture === 'function') {
            try {
                button.setPointerCapture(event.pointerId);
            } catch (err) {}
        }
        const delta = Number(button.dataset.durationStep);
        stopActiveHold = stop;
        stepBy(control, delta);
        delayTimer = setTimeout(() => {
            repeatTimer = setInterval(() => stepBy(control, delta), REPEAT_INTERVAL_MS);
        }, REPEAT_DELAY_MS);
    });

    ['pointerup', 'pointerleave', 'pointercancel', 'blur'].forEach((type) => {
        button.addEventListener(type, stop);
    });
}

function initControl(control) {
    if (control.dataset.durationControlReady === '1') {
        render(control);
        return;
    }
    control.dataset.durationControlReady = '1';

    const { value, slider, number, ongoing, steps } = parts(control);

    steps.forEach((step) => bindHold(control, step));

    if (slider) {
        slider.addEventListener('input', () => {
            number.value = slider.value;
            commit(control, Number(slider.value));
        });
    }

    number.addEventListener('input', () => {
        const digits = number.value.replace(/\D/g, '');
        if (number.value !== digits) {
            number.value = digits;
        }
        if (digits !== '') {
            commitNumeric(control, digits);
        }
    });

    number.addEventListener('blur', () => commitNumeric(control, number.value));

    number.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            commitNumeric(control, number.value);
        }
    });

    if (ongoing) {
        ongoing.addEventListener('change', () => {
            commit(control, ongoing.checked ? ONGOING_VALUE : lastNumericValue(control));
        });
    }

    value.addEventListener('change', () => {
        if (!internalUpdates.has(value)) {
            render(control);
        }
    });

    render(control);
}

function initDurationControls(root = document) {
    root.querySelectorAll('[data-duration-control]').forEach(initControl);
}

function resolveControl(target) {
    const element = typeof target === 'string' ? document.querySelector(target) : target;
    if (!element) {
        throw new Error(`duration-control: no element matched ${target}.`);
    }

    const control = element.closest('[data-duration-control]');
    if (!control) {
        throw new Error('duration-control: target is not inside a duration control.');
    }

    return control;
}

function setDurationControlDisabled(target, disabled) {
    const control = resolveControl(target);
    control.classList.toggle('is-disabled', !!disabled);
    render(control);
}

function setDurationControlValue(target, value) {
    commit(resolveControl(target), value);
}

function setDurationControlMax(target, max) {
    const control = resolveControl(target);
    if (control.dataset.baseMax === undefined) {
        control.dataset.baseMax = control.dataset.max;
    }
    const base = Number(control.dataset.baseMax);
    const next = Number(max);
    const resolved = Number.isFinite(next) && next >= minOf(control)
        ? Math.min(base, next)
        : base;
    control.dataset.max = String(resolved);
    reclampDurationControls(control.parentNode || document);
}

function setDurationControlOngoingAvailable(target, available) {
    const control = resolveControl(target);
    control.classList.toggle('duration-control--no-ongoing', !available);
    if (!available && isOngoing(control)) {
        commit(control, lastNumericValue(control));
    }
}

function clearDurationControls(scope = document) {
    scope.querySelectorAll('[data-duration-control]').forEach((control) => {
        delete control.dataset.lastNumeric;
        commit(control, null);
    });
}

function paintFrame(control, displayed, max) {
    const { slider, number } = parts(control);
    slider.max = String(max);
    slider.value = String(displayed);
    number.value = String(displayed);
    const min = Number(slider.min);
    const span = max - min;
    const filled = span > 0 ? ((displayed - min) / span) * 100 : 0;
    control.style.setProperty('--duration-fill', `${filled}%`);
}

function animateToValue(control, from, to, fromMax, toMax) {
    const { value } = parts(control);
    const startValue = value.value;
    const start = performance.now();
    const run = (clampRuns.get(control) || 0) + 1;
    clampRuns.set(control, run);
    const step = (now) => {
        if (clampRuns.get(control) !== run) {
            return;
        }
        if (!control.isConnected || value.value !== startValue) {
            control.classList.remove('is-clamping');
            return;
        }
        const progress = Math.min((now - start) / CLAMP_ANIMATION_MS, 1);
        const eased = 1 - (1 - progress) ** 3;
        paintFrame(
            control,
            Math.round(from + (to - from) * eased),
            Math.round(fromMax + (toMax - fromMax) * eased)
        );
        if (progress < 1) {
            requestAnimationFrame(step);
            return;
        }
        control.classList.remove('is-clamping');
        commit(control, to);
    };
    control.classList.add('is-clamping');
    requestAnimationFrame(step);
}

function reclampDurationControls(scope) {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    scope.querySelectorAll('[data-duration-control]').forEach((control) => {
        const { value, slider } = parts(control);
        if (value.value === '' || isOngoing(control)) {
            return;
        }

        const current = parseInt(value.value, 10);
        const clamped = clamp(control, current);
        if (clamped === current) {
            return;
        }

        if (reduceMotion || !slider) {
            commit(control, clamped);
            return;
        }

        animateToValue(control, Number(slider.value), clamped, Number(slider.max), maxOf(control));
    });
}

$(document)
    .off('change' + NS, 'select[name*="frequency"]')
    .on('change' + NS, 'select[name*="frequency"]', function () {
        reclampDurationControls(this.closest('form') || document);
    });

function initControlsOnInjectedMarkup() {
    if (document.body.dataset.durationControlObserver === '1') {
        return;
    }
    document.body.dataset.durationControlObserver = '1';

    let scanQueued = false;
    const queueScan = () => {
        if (scanQueued) {
            return;
        }
        scanQueued = true;
        requestAnimationFrame(() => {
            scanQueued = false;
            initDurationControls();
        });
    };

    new MutationObserver(queueScan).observe(document.body, { childList: true, subtree: true });
}

function start() {
    initDurationControls();
    initControlsOnInjectedMarkup();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
} else {
    start();
}

export {
    clearDurationControls,
    reclampDurationControls,
    setDurationControlDisabled,
    setDurationControlMax,
    setDurationControlOngoingAvailable,
    setDurationControlValue,
};
